import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon, Badge, Box, Button, Checkbox, Container, Divider, Group, Loader, NumberInput,
  Paper, SegmentedControl, Select, SimpleGrid, Stack, Switch, Table, Text, TextInput, Textarea, Title,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import HttpClient from "../helpers/HttpClient";
import { ArticleCategory } from "../types/article-category.type";
import {
  InventoryArticle, InventoryArticleInput, MOVEMENT_TYPE_LABELS, RentalFeeMode,
  StockMovementRow, StorageLocation, VariantStock, personName,
} from "../types/inventory.type";
import { StockMovementDialog } from "../components/inventory/StockMovementDialog";
import { ArticleRemoveDialog } from "../components/ArticleRemoveDialog";

type VariantRow = { id?: number; label: string; size: string; number: string; isActive: boolean };

const emptyVariant = (): VariantRow => ({ label: "", size: "", number: "", isActive: true });

function InventoryArticleEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [article, setArticle] = useState<InventoryArticle | null>(null);
  const [movements, setMovements] = useState<StockMovementRow[]>([]);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [locations, setLocations] = useState<StorageLocation[]>([]);
  const [bookingVariant, setBookingVariant] = useState<VariantStock | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [isStockTracked, setIsStockTracked] = useState(true);
  const [isRentable, setIsRentable] = useState(false);
  const [rentalFee, setRentalFee] = useState<number | string>("");
  const [rentalFeeMode, setRentalFeeMode] = useState<RentalFeeMode>("deposit");
  const [showInCalendar, setShowInCalendar] = useState(false);
  const [hasVariants, setHasVariants] = useState(false);
  const [variants, setVariants] = useState<VariantRow[]>([]);

  const applyArticle = (a: InventoryArticle) => {
    setArticle(a);
    setName(a.name);
    setDescription(a.description ?? "");
    setCategoryId(a.category ? String(a.category.id) : null);
    setLocationId(a.location ? String(a.location.id) : null);
    setIsStockTracked(a.isStockTracked);
    setIsRentable(a.isRentable);
    setRentalFee(a.rentalFee ?? "");
    setRentalFeeMode(a.rentalFeeMode);
    setShowInCalendar(a.showInCalendar);
    const active = a.variants.filter((v) => v.isActive);
    // A single "Standard" variant means the article has no real variants.
    setHasVariants(active.length > 1 || (active.length === 1 && active[0].label !== "Standard"));
    setVariants(
      active.map((v) => ({ id: v.id, label: v.label, size: v.size ?? "", number: v.number ?? "", isActive: true })),
    );
  };

  const load = useCallback(async () => {
    if (isNew) return;
    const client = new HttpClient<InventoryArticle>(`/inventory/article/${id}`);
    const [a, m] = await Promise.all([
      client.getSingle(),
      new HttpClient<StockMovementRow>(`/inventory/article/${id}/movements`).getAll(),
    ]);
    if (a) applyArticle(a);
    setMovements(m);
  }, [id, isNew]);

  useEffect(() => {
    new HttpClient<ArticleCategory>("/article-category").getAll().then(setCategories).catch(() => {});
    new HttpClient<StorageLocation>("/inventory/location").getAll().then(setLocations).catch(() => {});
    load()
      .catch(() => notifications.show({ color: "red", message: "Artikel konnte nicht geladen werden." }))
      .finally(() => setLoading(false));
  }, [load]);

  const save = async () => {
    if (!name.trim()) {
      notifications.show({ color: "red", message: "Bitte einen Namen eingeben." });
      return;
    }
    const payload: InventoryArticleInput = {
      name,
      description,
      categoryId: categoryId ? Number(categoryId) : null,
      locationId: locationId ? Number(locationId) : null,
      isStockTracked,
      isRentable,
      rentalFee: rentalFee === "" ? null : Number(rentalFee),
      rentalFeeMode,
      showInCalendar,
    };
    if (hasVariants) {
      const rows = variants.filter((v) => v.label.trim() || v.size.trim() || v.number.trim());
      if (rows.length === 0) {
        notifications.show({ color: "red", message: "Bitte mindestens eine Variante anlegen." });
        return;
      }
      payload.variants = rows.map((v) => ({
        id: v.id,
        label: v.label.trim(),
        size: v.size.trim() || undefined,
        number: v.number.trim() || undefined,
        isActive: true,
      }));
    }
    setSaving(true);
    try {
      const saved = isNew
        ? ((await new HttpClient("/inventory").postAction("article", payload)) as unknown as InventoryArticle)
        : ((await new HttpClient(`/inventory/article/${id}`).put(payload as never)).data as unknown as InventoryArticle);
      notifications.show({ color: "green", message: "Gespeichert." });
      if (isNew) navigate(`/inventory/article/${saved.id}`, { replace: true });
      else await load();
    } catch {
      notifications.show({ color: "red", message: "Speichern fehlgeschlagen." });
    } finally {
      setSaving(false);
    }
  };

  const restore = async () => {
    try {
      const restored = (await new HttpClient(`/inventory/article/${id}`).postAction("restore")) as unknown as InventoryArticle;
      setArticle(restored);
      notifications.show({ color: "green", message: "Artikel wiederhergestellt." });
    } catch {
      notifications.show({ color: "red", message: "Wiederherstellen fehlgeschlagen." });
    }
  };

  const updateVariant = (index: number, patch: Partial<VariantRow>) =>
    setVariants((prev) => prev.map((v, i) => (i === index ? { ...v, ...patch } : v)));

  if (loading) return <Loader mt="xl" />;

  const stockVariants = (article?.variants ?? []).filter((v) => v.isActive);

  return (
    <Container size="lg" mt="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>{isNew ? "Neuer Inventar-Artikel" : name}</Title>
        <Group>
          <Button variant="subtle" onClick={() => navigate("/inventory")}>Zurück</Button>
          {!isNew && article && !article.isArchived && (
            <Button color="red" variant="light" onClick={() => setRemoveOpen(true)}>Löschen / Archivieren</Button>
          )}
          <Button onClick={save} loading={saving}>Speichern</Button>
        </Group>
      </Group>

      {article?.isArchived && (
        <Paper withBorder p="sm" mb="md" bg="var(--mantine-color-orange-light)">
          <Group justify="space-between">
            <Text size="sm">
              Archiviert – erscheint nicht an der Kasse, im Verleih oder in den Listen. Historie bleibt erhalten.
            </Text>
            <Button size="xs" variant="light" color="orange" onClick={restore}>Wiederherstellen</Button>
          </Group>
        </Paper>
      )}

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Paper withBorder p="md">
          <Stack gap="sm">
            <TextInput label="Name" required value={name} onChange={(e) => setName(e.currentTarget.value)} />
            {article?.isRestaurantArticle && (
              <Text size="xs" c="dimmed">
                Kassen-Artikel – Preise und Kassen-Einstellungen unter „Rechnungen → Artikel“.
              </Text>
            )}
            <Textarea label="Beschreibung" autosize minRows={2} value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
            <Select
              label="Kategorie"
              clearable
              data={categories.map((c) => ({ value: String(c.id), label: c.name }))}
              value={categoryId}
              onChange={setCategoryId}
            />
            <Select
              label="Lagerort"
              description="Lagerorte werden unter „Inventar → Lagerorte“ verwaltet."
              clearable
              data={locations.map((l) => ({ value: String(l.id), label: l.name }))}
              value={locationId}
              onChange={setLocationId}
            />
          </Stack>
        </Paper>

        <Paper withBorder p="md">
          <Stack gap="sm">
            <Switch
              label="Bestand führen"
              description="Mengen werden gezählt (Anfangsbestand, Zugänge, Abgänge)."
              checked={isStockTracked}
              onChange={(e) => setIsStockTracked(e.currentTarget.checked)}
            />
            <Switch
              label="Verleihbar"
              description="Kann reserviert und an Personen ausgegeben werden."
              checked={isRentable}
              onChange={(e) => setIsRentable(e.currentTarget.checked)}
            />
            {isRentable && (
              <>
                <NumberInput
                  label="Leihgebühr (€)"
                  description="Wird bei der Ausgabe dem Konto des Mitglieds belastet. Leer = keine Gebühr."
                  value={rentalFee}
                  onChange={setRentalFee}
                  min={0}
                  decimalScale={2}
                  decimalSeparator=","
                  fixedDecimalScale
                />
                <SegmentedControl
                  value={rentalFeeMode}
                  onChange={(v) => setRentalFeeMode(v as RentalFeeMode)}
                  data={[
                    { value: "deposit", label: "Kaution (bei Rückgabe erstattet)" },
                    { value: "fee", label: "Gebühr (bleibt)" },
                  ]}
                />
                <Checkbox
                  label="Ausleihen im Kalender anzeigen"
                  description="z.B. Mannschaftsbus, Kamera. Nur für Benutzer mit Inventar-Recht sichtbar."
                  checked={showInCalendar}
                  onChange={(e) => setShowInCalendar(e.currentTarget.checked)}
                />
              </>
            )}
          </Stack>
        </Paper>
      </SimpleGrid>

      <Paper withBorder p="md" mt="md">
        <Group justify="space-between" mb="sm">
          <Title order={4}>Varianten</Title>
          <Switch
            label="Artikel hat Varianten (z.B. Größe / Nummer)"
            checked={hasVariants}
            onChange={(e) => {
              const on = e.currentTarget.checked;
              setHasVariants(on);
              if (on && variants.length === 0) setVariants([emptyVariant()]);
            }}
          />
        </Group>
        {hasVariants ? (
          <Stack gap="xs">
            {variants.map((v, i) => (
              <Group key={v.id ?? `new-${i}`} gap="xs" wrap="nowrap">
                <TextInput placeholder="Größe" value={v.size} onChange={(e) => updateVariant(i, { size: e.currentTarget.value })} w={100} />
                <TextInput placeholder="Nummer" value={v.number} onChange={(e) => updateVariant(i, { number: e.currentTarget.value })} w={100} />
                <TextInput
                  placeholder="Bezeichnung (optional, sonst Größe / #Nummer)"
                  value={v.label}
                  onChange={(e) => updateVariant(i, { label: e.currentTarget.value })}
                  style={{ flex: 1 }}
                />
                <ActionIcon color="red" variant="subtle" onClick={() => setVariants((prev) => prev.filter((_, j) => j !== i))} title="Entfernen">
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
            <Box>
              <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} onClick={() => setVariants((prev) => [...prev, emptyVariant()])}>
                Variante hinzufügen
              </Button>
            </Box>
            <Text size="xs" c="dimmed">
              Entfernte Varianten werden deaktiviert, ihr Bestand und ihre Historie bleiben erhalten.
            </Text>
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">Keine Varianten – der Bestand wird für den Artikel als Ganzes geführt.</Text>
        )}
      </Paper>

      {!isNew && article && (
        <Paper withBorder p="md" mt="md">
          <Title order={4} mb="sm">Bestand</Title>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Variante</Table.Th>
                <Table.Th ta="right">Bestand</Table.Th>
                <Table.Th ta="right">Ausgegeben</Table.Th>
                <Table.Th ta="right">Verfügbar</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {stockVariants.map((v) => (
                <Table.Tr key={v.id}>
                  <Table.Td>{v.label}</Table.Td>
                  <Table.Td ta="right" c={v.stock <= 0 ? "red" : undefined}>{v.stock}</Table.Td>
                  <Table.Td ta="right">{v.out || "–"}</Table.Td>
                  <Table.Td ta="right" c={v.available <= 0 ? "red" : undefined}>{v.available}</Table.Td>
                  <Table.Td ta="right">
                    <Button size="xs" variant="light" onClick={() => setBookingVariant(v)}>Bestand buchen</Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Divider my="md" label="Buchungen" labelPosition="left" />
          {movements.length === 0 ? (
            <Text size="sm" c="dimmed">Noch keine Buchungen.</Text>
          ) : (
            <Table striped>
              <Table.Tbody>
                {movements.map((m) => (
                  <Table.Tr key={m.id}>
                    <Table.Td w={140}>{dayjs(m.movementDate).format("DD.MM.YYYY HH:mm")}</Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color={m.quantity < 0 ? "red" : "green"}>
                        {MOVEMENT_TYPE_LABELS[m.type] ?? m.type}
                      </Badge>
                    </Table.Td>
                    {stockVariants.length > 1 && <Table.Td>{m.variantLabel}</Table.Td>}
                    <Table.Td ta="right" fw={600}>{m.quantity > 0 ? `+${m.quantity}` : m.quantity}</Table.Td>
                    <Table.Td c="dimmed">{m.note}</Table.Td>
                    <Table.Td c="dimmed">{personName(m.createdBy)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      )}

      <ArticleRemoveDialog
        article={removeOpen && article ? { id: article.id, name: article.name } : null}
        basePath="/inventory/article"
        onClose={() => setRemoveOpen(false)}
        onDone={() => navigate("/inventory")}
      />

      <StockMovementDialog
        variant={bookingVariant}
        articleName={name}
        onClose={() => setBookingVariant(null)}
        onSaved={() => load()}
      />
    </Container>
  );
}

export default InventoryArticleEdit;
